/* eslint-disable @typescript-eslint/quotes */
import { ChangeDetectionStrategy, Component, DestroyRef, inject, Input, signal } from '@angular/core';
import { MaterialAnnotationListFooterComponent } from './annotation-list-footer/annotation-list-footer.component';
import { MaterialAnnotationListHeaderComponent } from './annotation-list-header/annotation-list-header.component';
import { MaterialAnnotationListContentComponent } from './annotations-list-content/annotations-list-content.component';
import {
  EditModeSelectionState,
  MaterialAnnotationType,
  MaterialDropdownState,
} from './material-annotation-list.model';
import { MaterialAnnotationsListService } from './material-annotations-list.service';
import { ConfirmationModalComponent, ConfirmationModalData } from '../modals/confirmation-modal';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs/operators';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'rdrx-material-annotations-list',
  standalone: true,
  host: { 'data-automation-id': 'material-annotations-list-modal' },
  template: `
    <!-- HEADER -->
    <rdrx-material-annotations-list-header
      [type]="type"
      [headerClass]="true"
      [isEditMode]="editModeActive()"
      [editModeSelections]="editModeSelections()"
      [isEmpty]="isEmpty()"
      (dropdownOptionSelected)="dropdownSelected.set($event)"
      (allAnnotationsSelected)="MAnnotationsService.selectAllAnnotationsInList($event)"
      (editModeSelected)="switchEditMode(!editModeActive())"
      (exportAnnotations)="beginExport($event)"
    ></rdrx-material-annotations-list-header>
    <!-- CONTENT -->
    <div class="content">
      <rdrx-material-annotations-list-content
        [type]="type"
        [isEditMode]="editModeActive()"
        [filterState]="dropdownSelected()"
        (spineIdExpanded)="MAnnotationsService.spineIdExpanded($event)"
        (annotationListItemClicked)="MAnnotationsService.annotationListItemClicked($event)"
        (deleteAnnotationListItem)="MAnnotationsService.deleteAnnotationListItem($event)"
        (annotationListSpineSelected)="MAnnotationsService.selectAllAnnotationsBySpine($event)"
        (annotationListResourceSelected)="MAnnotationsService.selectAnnotationByResource($event)"
        (editModeSelections)="editModeState($event)"
        (hasAnnotations)="isEmpty.set(!$event)"
      ></rdrx-material-annotations-list-content>
      <!-- FOOTER -->
       @if (editModeActive()) {
        <rdrx-material-annotations-list-footer
          [isEditMode]="editModeActive()"
          [disableDeleteBtn]="false"
          (deleteClick)="MAnnotationsService.bulkDelete(type)"
          (cancelClick)="disableEditMode()"
        ></rdrx-material-annotations-list-footer>
      }
    </div>
  `,
  styles: `
    :host {
      display: flex;
      align-content: space-between;
      flex-direction: column;
      height: 100%;
    }
    .content {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      height: 100%;
    }
  `,
  imports: [
    MaterialAnnotationListHeaderComponent,
    MaterialAnnotationListContentComponent,
    MaterialAnnotationListFooterComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MaterialAnnotationsListComponent {
  readonly MAnnotationsService = inject(MaterialAnnotationsListService);
  readonly dialog = inject(MatDialog);
  readonly translate = inject(TranslateService);
  readonly destroyRef = inject(DestroyRef);

  @Input({ required: true }) type!: MaterialAnnotationType;

  isEmpty = signal(true);

  editModeActive = signal(false);
  editModeSelections = signal<EditModeSelectionState>({ someSelected: false, allSelected: false });
  dropdownSelected = signal<MaterialDropdownState>(MaterialDropdownState.ALL);

  disableEditMode(): void {
    this.editModeActive.set(false);
    this.MAnnotationsService.clearSelectedAnnotations();
  }

  switchEditMode(enable: boolean): void {
    enable ? this.editModeActive.set(true) : this.disableEditMode();
  }

  editModeState(state: EditModeSelectionState): void {
    this.editModeSelections.set(state);
  }

  beginExport(type: MaterialAnnotationType): void {
    const dialogRef = this.openConfirmationDialog();
    dialogRef.afterClosed().pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(result => {
        if (result) {
          this.MAnnotationsService.exportAnnotations(type);
          this.MAnnotationsService.completeExport(type);
        }
      }),
    ).subscribe();
  }

  private openConfirmationDialog(): MatDialogRef<ConfirmationModalComponent, boolean> {
    const data: ConfirmationModalData = {
      title: this.translate.instant('export.export'),
      content: this.translate.instant('export.complete'),
      closeText: this.translate.instant('shared.cancel'),
      confirmText: this.translate.instant('export.download'),
    };
    return this.dialog.open(ConfirmationModalComponent, {
      data,
      ariaLabelledBy: 'confirmation-modal-h2',
    });
  }
}
